// Named main-battery rounds (config.shells) — the client half of ShellConfig
// in internal/game/config.go.
//
// A warship's mainGun can name the shell it loads ("shell": "type91") and
// leave its muzzle speed, damage, penetration and fire chance to it. The
// server fills those in when it parses the config; the browser reads the same
// raw config.json, so it has to fill them the same way, once, before anything
// reads a mainGun — the gunsight ranges on muzzleSpeedMS, the hangar card
// grades on apDamage. resolveShells is that one place.
//
// A round past a 16-inch is a super-heavy: drawn at its true size, burning a
// longer tracer (the server flags it on the wire, so every client agrees
// from the frame it first shows), and throwing up a taller column where it
// falls (the splash event names the shell; superHeavy reads its bore here).
// The Yamato's 18.1-inch, the heaviest shell ever fired at sea, should look
// it from across the Brick Sea.

// A bore past this (mm) is a super-heavy round: the Iowa's 16-inch (406 mm)
// is the yardstick every other shell is drawn against. Mirrors
// superHeavyShellMM in internal/game/config.go.
export const SUPER_HEAVY_MM = 420;

// Every roster a mainGun can sail in: the served one, the theater rosters the
// server swaps in, and each era's.
function rosters(config) {
  const out = [config.groundVehicles?.types, config.naval?.ships, config.land?.vehicles];
  for (const era of Object.values(config.eras ?? {})) out.push(era?.ships, era?.vehicles);
  return out.filter(Array.isArray);
}

/** Fill every mainGun that names a shell from config.shells (mutates; zeros take the shell's). */
export function resolveShells(config) {
  const byName = new Map((config?.shells ?? []).map((s) => [s.name, s]));
  if (!byName.size) return config;
  for (const list of rosters(config)) {
    for (const t of list) {
      const mg = t?.mainGun;
      const sh = mg?.shell ? byName.get(mg.shell) : null;
      if (!sh) continue;
      for (const k of ["muzzleSpeedMS", "apDamage", "apPenMM", "heDamage", "heFireChance"]) {
        if (!(mg[k] > 0) && sh[k] > 0) mg[k] = sh[k];
      }
    }
  }
  return config;
}

/** The config.shells entry by name (null for the generic round, or an unknown one). */
export function shellNamed(config, name) {
  if (!name) return null;
  return (config?.shells ?? []).find((s) => s.name === name) ?? null;
}

/** Whether a named round is a super-heavy, drawn and splashed to its size. */
export function superHeavy(config, name) {
  return (shellNamed(config, name)?.caliberMM ?? 0) > SUPER_HEAVY_MM;
}
